import PropTypes from 'prop-types'
import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react'

import { C, clamp, lerp, prog, spring } from '../../clip-geometry'
import { BX, BY, SH, SW, blink, lookDir } from '../stage'

// Variante "Buddy": ein knuffiger persoenlicher Assistent mit Gesicht und Aermchen.
// Lugt von unten rein, schaut sich um, entdeckt dich, huepft und winkt.

const R = 27
const INTRO_END = 2.8
const SPARK =
  'M0 -14 C1.6 -4 4 -1.6 14 0 C4 1.6 1.6 4 0 14 C-1.6 4 -4 1.6 -14 0 C-4 -1.6 -1.6 -4 0 -14Z'

// Weicher, leicht wabbelnder Koerper als geschlossene Catmull-Rom-Kurve
function blobPath(cx, cy, r, t, sx, sy, wob) {
  const n = 36
  const pts = []
  for (let k = 0; k < n; k++) {
    const a = (k / n) * Math.PI * 2
    const rr =
      r *
      (1 +
        wob *
          (0.035 * Math.sin(3 * a + t * 2.1) +
            0.025 * Math.sin(5 * a - t * 1.6)))
    pts.push([cx + Math.cos(a) * rr * sx, cy + Math.sin(a) * rr * sy])
  }
  let d = `M${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)}`
  for (let k = 0; k < n; k++) {
    const p0 = pts[(k - 1 + n) % n]
    const p1 = pts[k]
    const p2 = pts[(k + 1) % n]
    const p3 = pts[(k + 2) % n]
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6]
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6]
    d += `C${c1[0].toFixed(2)} ${c1[1].toFixed(2)} ${c2[0].toFixed(2)} ${c2[1].toFixed(2)} ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`
  }
  return d + 'Z'
}

function frame(t, ui, el) {
  const d = lookDir(ui)

  // Reinlugen, Huepfer, danach Atmen
  const rise = spring(t - 0.05, 7, 11)
  let cy = lerp(BY + 78, BY, rise)
  let sx = 1
  let sy = 1
  if (t < 0.7) {
    sy = 1 + 0.15 * (1 - rise)
    sx = 1 - 0.1 * (1 - rise)
  }
  const hp = prog(t, 1.5, 1.95)
  if (hp > 0 && hp < 1) {
    cy -= 14 * Math.sin(Math.PI * hp)
    sy *= 1 + 0.08 * Math.sin(Math.PI * hp)
  }
  const land = t - 1.95
  if (land > 0 && land < 0.8) {
    const q = 1 - 0.18 * Math.exp(-9 * land) * Math.cos(16 * land)
    sy *= q
    sx *= 2 - q
  }
  const idle = clamp((t - 2.0) * 2)
  sy *= 1 - 0.022 * Math.sin(t * 2.4) * idle
  sx *= 1 + 0.022 * Math.sin(t * 2.4) * idle
  // Hover: freudig squishen
  sx *= 1 + 0.07 * ui.hover
  sy *= 1 + 0.03 * ui.hover
  // Unterkante bleibt am Boden
  cy += R * (1 - sy)
  const lean = d[0] * 2.5 * ui.near
  const cx = BX + lean

  el.body.setAttribute('d', blobPath(cx, cy, R, t, sx, sy, 1))

  // Blick: umschauen im Intro, sonst Maus, beim Nachdenken nach oben
  let lk = [Math.sin(t * 0.7) * 1.4, Math.cos(t * 0.5) * 0.8]
  if (t < INTRO_END) {
    lk = [0, 0]
    if (t > 0.7) lk = [-2.2, 0.4]
    if (t > 1.0) lk = [2.2, 0.4]
    if (t > 1.3) lk = [-1.6, -1.6]
  }
  lk = [lerp(lk[0], d[0] * 2.3, ui.near), lerp(lk[1], d[1] * 2.3, ui.near)]
  lk = [lerp(lk[0], 1.8, ui.thinking), lerp(lk[1], -2.2, ui.thinking)]
  const surprise = 1 + 0.18 * Math.sin(Math.PI * prog(t, 1.3, 1.6))
  const happy = Math.max(
    ui.open * (1 - ui.thinking),
    prog(t, 1.95, 2.05) * (1 - prog(t, 2.6, 2.75))
  )
  const eyeOpen = blink(t, 4.6, 1.3) * surprise * (1 - happy)

  el.face.setAttribute(
    'transform',
    `translate(${cx.toFixed(2)} ${(cy + (1 - sy) * 4).toFixed(2)}) scale(${sx.toFixed(3)} ${sy.toFixed(3)})`
  )
  el.eyes.forEach((g, i) => {
    g.setAttribute(
      'transform',
      `translate(${i ? 8.5 : -8.5} -4) scale(${surprise.toFixed(3)} ${Math.max(0, eyeOpen).toFixed(3)})`
    )
    g.lastChild.setAttribute(
      'transform',
      `translate(${lk[0].toFixed(2)} ${lk[1].toFixed(2)})`
    )
  })
  el.happy.style.opacity = happy > 0.5 ? 1 : 0

  // Mund: Laecheln, beim Hover breit, beim Nachdenken nachdenklich
  const w = lerp(lerp(4.6, 7, Math.max(ui.hover, happy)), 2.6, ui.thinking)
  const dep = lerp(lerp(3.8, 6.5, Math.max(ui.hover, happy)), -0.6, ui.thinking)
  const my = 8 + ui.thinking * 0.8
  el.mouth.setAttribute(
    'd',
    `M${(-w + ui.thinking * 1.5).toFixed(2)} ${my}Q0 ${(my + dep).toFixed(2)} ${w.toFixed(2)} ${(my - ui.thinking * 0.6).toFixed(2)}`
  )

  // Aermchen: haengen, winken im Intro und beim Hover
  const waveI = prog(t, 1.95, 2.1) * (1 - prog(t, 2.65, 2.8))
  const wave = Math.max(waveI, ui.hover * 0.7)
  const base = [cx + R * sx * 0.92, cy + 4]
  const aR = lerp(65, -55 + 22 * Math.sin(t * 15), wave)
  const ar = (aR * Math.PI) / 180
  el.armR.setAttribute(
    'd',
    `M${base[0]} ${base[1]}L${base[0] + Math.cos(ar) * 11} ${base[1] + Math.sin(ar) * 11}`
  )
  const baseL = [cx - R * sx * 0.92, cy + 4]
  const al = ((180 - 65 + 8 * Math.sin(t * 2.4) * idle) * Math.PI) / 180
  el.armL.setAttribute(
    'd',
    `M${baseL[0]} ${baseL[1]}L${baseL[0] + Math.cos(al) * 10} ${baseL[1] + Math.sin(al) * 10}`
  )
  const armsOn = t > 0.35 ? 1 : 0
  el.armR.style.opacity = armsOn
  el.armL.style.opacity = armsOn

  // Funkeln als KI-Hinweis
  const sp = spring(t - 2.1, 8, 14)
  el.spark.setAttribute(
    'transform',
    `translate(${(cx + 22).toFixed(2)} ${(cy - R * sy - 4).toFixed(2)}) rotate(${(1 - sp) * -120}) scale(${(sp * (0.55 + 0.08 * Math.sin(t * 3.1))).toFixed(3)})`
  )

  // Denkblasen beim Nachdenken
  el.thoughts.forEach((c, i) => {
    const on = ui.thinking
    const pulse = 1 + 0.25 * Math.max(0, Math.sin(t * 7 - i * 0.9))
    c.setAttribute('cx', cx - 14 - i * 7)
    c.setAttribute('cy', cy - R * sy - 6 - i * 8)
    c.setAttribute('r', Math.max(0, (2 + i * 1.2) * on * pulse))
  })
}

const BuddyStage = forwardRef(function BuddyStage(_, ref) {
  const svgRef = useRef(null)
  const elRef = useRef(null)

  useLayoutEffect(() => {
    const svg = svgRef.current
    const q = (s) => svg.querySelector(s)
    elRef.current = {
      body: q('.ab-body'),
      face: q('.ab-face'),
      eyes: Array.from(svg.querySelectorAll('.ab-eye')),
      happy: q('.ab-happy'),
      mouth: q('.ab-mouth'),
      armR: q('.ab-armR'),
      armL: q('.ab-armL'),
      spark: q('.ab-spark'),
      thoughts: Array.from(svg.querySelectorAll('.ab-thought'))
    }
  }, [])

  useImperativeHandle(ref, () => ({
    frame: (t, ui) => elRef.current && frame(t, ui, elRef.current)
  }))

  return (
    <svg
      ref={svgRef}
      width={SW}
      height={SH}
      viewBox={`0 0 ${SW} ${SH}`}
      className='absolute inset-0 overflow-visible'
      aria-hidden='true'
    >
      {[0, 1, 2].map((i) => (
        <circle
          key={i}
          className='ab-thought'
          r='0'
          fill='#DCE6FF'
          stroke={C.blue}
          strokeWidth='1.2'
        />
      ))}
      <path
        className='ab-armL'
        fill='none'
        stroke={C.blue}
        strokeWidth='5'
        strokeLinecap='round'
        opacity='0'
      />
      <path
        className='ab-armR'
        fill='none'
        stroke={C.blue}
        strokeWidth='5'
        strokeLinecap='round'
        opacity='0'
      />
      <path className='ab-body' fill={C.blue} />
      <g className='ab-face'>
        <ellipse cx='-13.5' cy='5' rx='3.4' ry='2.2' fill={C.pink} />
        <ellipse cx='13.5' cy='5' rx='3.4' ry='2.2' fill={C.pink} />
        {[0, 1].map((i) => (
          <g key={i} className='ab-eye'>
            <ellipse rx='5.2' ry='6.2' fill='#fff' />
            <circle r='2.7' fill={C.dark} />
          </g>
        ))}
        <g className='ab-happy' opacity='0'>
          <path
            d='M-12.5 -3 Q-8.5 -8.5 -4.5 -3'
            fill='none'
            stroke='#fff'
            strokeWidth='2.4'
            strokeLinecap='round'
          />
          <path
            d='M4.5 -3 Q8.5 -8.5 12.5 -3'
            fill='none'
            stroke='#fff'
            strokeWidth='2.4'
            strokeLinecap='round'
          />
        </g>
        <path
          className='ab-mouth'
          fill='none'
          stroke='#fff'
          strokeWidth='2.2'
          strokeLinecap='round'
        />
      </g>
      <path className='ab-spark' d={SPARK} fill={C.yellow} />
    </svg>
  )
})

export function BuddyAvatar({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox='-30 -30 60 60' aria-hidden='true'>
      <circle r='28' fill={C.blue} />
      <ellipse cx='-13.5' cy='5' rx='3.4' ry='2.2' fill={C.pink} />
      <ellipse cx='13.5' cy='5' rx='3.4' ry='2.2' fill={C.pink} />
      {[-8.5, 8.5].map((x) => (
        <g key={x} transform={`translate(${x} -4)`}>
          <ellipse rx='5.2' ry='6.2' fill='#fff' />
          <circle r='2.7' fill={C.dark} />
        </g>
      ))}
      <path
        d='M-4.6 8 Q0 11.8 4.6 8'
        fill='none'
        stroke='#fff'
        strokeWidth='2.2'
        strokeLinecap='round'
      />
    </svg>
  )
}

BuddyAvatar.propTypes = { size: PropTypes.number }

export default {
  id: 'buddy',
  name: 'Buddy',
  description:
    'Ein knuffiger persönlicher Assistent mit Gesicht, Bäckchen und Ärmchen. Er lugt von unten rein, schaut sich um, entdeckt dich, hüpft und winkt. Danach folgen seine Augen der Maus. Beim Nachdenken schaut er nach oben und hat Denkblasen, bei offenem Chat lächelt er mit geschlossenen Augen.',
  introEnd: INTRO_END,
  Stage: BuddyStage,
  Avatar: BuddyAvatar
}
